import React, { useState } from 'react';
import Sidebar from 'components/Sidebar';
import MobileHeader from 'components/MobileHeader';
import SearchOverlay from 'components/SearchOverlay';
import LoadingOverlay from 'components/LoadingOverlay';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAgent } from 'api/agents';
import Container from 'components/Container';
import { UploadIcon } from '@phosphor-icons/react';
import { XIcon } from '@phosphor-icons/react/dist/ssr';
import Button from 'components/Button';
import { startAgent } from '../api/start';
import qs from 'query-string';

export default function Agent() {

    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const { agentId } = useParams();
    const { conversation: conversationId } = qs.parse(window.location.search, { ignoreQueryPrefix: true });

    const [ images, setImages ] = useState([]);

    const navigate = useNavigate();
    const cache = useQueryClient();

    const { data: agent, isLoading } = useQuery({
        queryKey: ['agents', agentId],
        queryFn: async () => await getAgent({
            id: agentId
        }),
    });

    const { mutate, status } = useMutation({
        mutationKey: [agent?.slug + '-start'],
        mutationFn: startAgent,
        onSuccess: (data) => {
            if(data?.conversation) {
                cache.invalidateQueries({ queryKey: ['conversations', data.conversation._id] });
                return navigate(`/agents/${agent?.slug}/${agentId}/conversations/${data.conversation._id}`);
            }
        },
        onError: (error) => {
            console.error('Error starting agent:', error);
        }
    });

    const onStartAgentHandler = () => {
        if(status === 'loading') return;
        mutate({
            files: images,
            agent,
            body: {
                conversation: conversationId
            }
        });
    };

    const handleFileChange = (event) => {
        const files = event.target.files;
        for (const file of files) {
            setImages(prev => [...prev, file]);
        }
    }

    return (
        <>
            <Sidebar
                visible={sidebar}
                setVisible={setSidebar}
            />

            <SearchOverlay
                visible={search}
                setVisible={setSearch}
            />

            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                search={search}
                setSearch={setSearch}
            />

            <LoadingOverlay isLoading={isLoading} />
            <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-auto pt-20 md:pt-14 pb-20 ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                <Container>
                    <div className='w-full flex items-center gap-5'>
                        <div className='w-12 min-w-12 md:w-24 h-24 rounded-full bg-center bg-cover' style={{ backgroundImage: `url(${agent?.image || '/images/agent-avatar.jpg'})` }}></div>
                        <div>
                            <h1 className='text-xl md:text-3xl font-medium text-black pb-1'>{agent?.name}</h1>
                            <p className='text-muted'>{agent?.description}</p>
                        </div>
                    </div>
                    <div className='flex items-start gap-10 pt-10'>
                        <div className='w-full'>
                            <h3 className='text-black text-xl mb-4'>Immagini caricate</h3>

                            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                                <form className='flex flex-col gap-4'>
                                    <div className="flex items-center justify-center w-full">
                                        <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border border-light border-dashed rounded-lg cursor-pointer hover:bg-verylight">
                                            <div className="flex flex-col items-center justify-center p-3 md:p-6">
                                                <UploadIcon size={40} className="mb-2 text-muted" />
                                                <p className="text-sm text-center text-muted"><span className="font-semibold">Clicca</span> per caricare o trascina qui</p>
                                            </div>
                                            <input id="dropzone-file" type="file" className="hidden" multiple onChange={handleFileChange}/>
                                        </label>
                                    </div> 
                                </form>

                                {images.map((file, index) => {
                                    const url = URL.createObjectURL(file);
                                    return (
                                        <div key={index} className='relative group'>
                                            <img
                                                src={url}
                                                alt={`Preview ${index + 1}`}
                                                className='w-full h-32 object-cover rounded-lg border border-grey'
                                            />
                                            <div className='absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center'>
                                                <button
                                                    onClick={() => setImages(images.filter((_, i) => i !== index))}
                                                    className='text-white bg-red-500 rounded-full p-2 hover:bg-red-600 transition-colors cursor-pointer'
                                                >
                                                    <XIcon size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {!!images.length && (
                        <div className='w-full pt-10 flex'>
                            <Button
                                component="button"
                                onClick={onStartAgentHandler}
                                className={`${status === 'pending' ? 'opacity-50 cursor-not-allowed' : ''} ${status === 'success' ? 'hidden' : ''} ${status === 'error' ? '!bg-red-400 hover:!bg-red-500 text-white' : ''}`}
                            >
                                {status === 'error' && `Si è verificato un errore. Riprova`}
                                {status === 'pending' && `Caricamento...`}
                                {status === 'success' && `Avvia l'agente con le immagini caricate`}
                                {status === 'idle' && `Avvia l'agente con le immagini caricate`}
                            </Button>
                        </div>
                    )}
                </Container>
            </div>
        </>
    )
}
